import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
  MatAutocomplete
} from '@angular/material/autocomplete';
import {
  MatChipInputEvent,
  MatChipGrid,
  MatChipRow,
  MatChipRemove,
  MatChipInput
} from '@angular/material/chips';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose
} from '@angular/material/dialog';
import { map, Observable, startWith } from 'rxjs';
import { ColumnDefinition } from '../../../dto/column-definition';
import { SearchParameter } from '../../../dto/search-parameter';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { NgFor, AsyncPipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatOption } from '@angular/material/core';
import { MatButton } from '@angular/material/button';

@Component({
  selector: 'pcp-text-filter',
  templateUrl: './text-filter.component.html',
  styleUrls: ['./text-filter.component.scss'],
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    MatFormField,
    MatLabel,
    MatChipGrid,
    NgFor,
    MatChipRow,
    MatChipRemove,
    MatIcon,
    FormsModule,
    MatChipInput,
    MatAutocompleteTrigger,
    ReactiveFormsModule,
    MatAutocomplete,
    MatOption,
    MatDialogActions,
    MatButton,
    MatDialogClose,
    AsyncPipe
  ]
})
export class TextFilterComponent {
  constructor(
    public dialogRef: MatDialogRef<TextFilterComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      col: ColumnDefinition;
      values: string[];
      autoComplete: string[];
    }
  ) {
    if (!data.values) {
      data.values = [];
    }
    this.filteredKeywords = this.keywordCtrl.valueChanges.pipe(
      startWith(null),
      map((keyword: string | null) =>
        keyword ? this._filter(keyword) : this.data.autoComplete.slice()
      )
    );
  }

  @ViewChild('keywordInput') keywordInput: ElementRef<HTMLInputElement> | null =
    null;
  separatorKeysCodes: number[] = [ENTER, COMMA];
  keywordCtrl = new FormControl('');
  filteredKeywords: Observable<string[]>;

  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    if (value) {
      this.data.values.push(value);
    }

    // Clear the input value
    event.chipInput.clear();

    this.keywordCtrl.setValue(null);
  }

  remove(keyword: string): void {
    const index = this.data.values.indexOf(keyword);

    if (index >= 0) {
      this.data.values.splice(index, 1);
    }
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    this.data.values.push(event.option.viewValue);
    if (this.keywordInput) {
      this.keywordInput.nativeElement.value = '';
    }
    this.keywordCtrl.setValue(null);
  }

  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase();

    return this.data.autoComplete.filter((keyword) =>
      keyword.toLowerCase().includes(filterValue)
    );
  }

  capitalizeFirstLetter(name: string) {
    return name.charAt(0).toUpperCase() + name.slice(1);
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.dialogRef.close(this.getParameter());
    }
  }

  getParameter() {
    return new SearchParameter(
      'search' + this.capitalizeFirstLetter(this.data.col.name),
      this.data.values ?? [],
      this.data.col.type
    );
  }
}
